import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Loader2, Send } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";

const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });
};

/**
 * Referrals in a participant's file, both ways:
 *
 *  - their own code: when it was sent, how many colleagues enrolled with it,
 *    and "Trimite codul de recomandare" — the first time for somebody who
 *    paid before the offer began, or again for somebody who lost the email;
 *  - who referred them: the code they came with — or, when the colleague
 *    forgot to enter it, a field to link the enrolment by hand.
 *
 * Renders nothing where there is nothing to say: a place that gets no code
 * (a private cohort, a place paid by a school, Curs FPED), or the offer off
 * with no code ever made. The server decides (`show`).
 */
export default function ReferralCodePanel({ enrollmentId, canSend }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [linkCode, setLinkCode] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/referrals/enrollment/${enrollmentId}`);
      setState(r.data);
    } catch {
      setState({ show: false });
    }
  }, [enrollmentId]);

  useEffect(() => { load(); }, [load]);

  if (!state?.show) return null;

  const send = async () => {
    const question = state.sent_at
      ? "Trimiți din nou emailul cu codul de recomandare? Codul și data-limită rămân aceleași."
      : "Trimiți emailul cu codul de recomandare? Zilele de valabilitate curg de azi.";
    if (!window.confirm(question)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/referrals/send-code/${enrollmentId}`);
      toast.success(`Am trimis codul ${r.data.code}, valabil până la ${r.data.until}.`);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Codul nu a putut fi trimis."));
    } finally {
      setBusy(false);
    }
  };

  // Link by hand. A refusal with 412 means "made after the code's last day":
  // the team decides, so it is asked once and sent again.
  const link = async (allowLate = false) => {
    const code = linkCode.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/referrals/link", { enrollment_id: enrollmentId, code, allow_late: allowLate });
      toast.success(`Înscrierea e legată de codul ${r.data.code}: ${r.data.valid_count} din ${r.data.required} recomandări plătite.`);
      setLinkCode("");
      await load();
    } catch (err) {
      const message = apiErrorMessage(err, "Legătura nu a putut fi făcută.");
      if (err?.response?.status === 412 && !allowLate) {
        setBusy(false);
        if (window.confirm(`${message} O legi oricum?`)) await link(true);
        return;
      }
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm("Scoți legătura cu acest cod? Înscrierea rămâne, dar nu mai contează ca recomandare.")) return;
    setBusy(true);
    try {
      await api.post("/admin/referrals/unlink", { enrollment_id: enrollmentId });
      toast.success("Legătura a fost scoasă.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Legătura nu a putut fi scoasă."));
    } finally {
      setBusy(false);
    }
  };

  const by = state.referred_by;
  const referred = state.referred;

  return (
    <div className="rounded-xl border border-[#E5E7EB] p-4" data-testid="enr-referral-code-panel">
      <div className="mb-2 flex items-center gap-2 font-semibold text-[#373A45]">
        <Gift size={16} className="text-[#965EA7]" /> Recomandări
      </div>

      {/* Who referred them. */}
      <div className="mb-3 text-sm text-[#373A45]" data-testid="enr-referred-by">
        {by ? (
          <>
            Recomandat de <strong>{by.name || by.email || "un cursant"}</strong>, cu codul{" "}
            <span className="font-mono font-semibold tracking-widest text-[#7A4986]">{by.code}</span>
            <span className="ml-1 text-xs text-[#6B7280]">
              · {by.counts ? "contează, plata e confirmată" : "nu contează încă, plata nu e confirmată"}
              {by.manual ? " · legat de echipă" : ""}
            </span>
            {canSend && (
              <button type="button" onClick={unlink} disabled={busy}
                className="ml-2 text-xs text-[#9b1f2c] underline disabled:opacity-50" data-testid="enr-referral-unlink">
                Scoate legătura
              </button>
            )}
          </>
        ) : (
          <span className="text-[#6B7280]">Nu a venit printr-o recomandare.</span>
        )}
        {!by && canSend && state.can_link && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={linkCode}
              onChange={(e) => setLinkCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16))}
              onKeyDown={(e) => e.key === "Enter" && link()}
              placeholder="Codul colegului"
              aria-label="Codul de recomandare al colegului"
              className="w-40 rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-1.5 font-mono text-sm tracking-widest text-[#373A45] outline-none focus:ring-2 focus:ring-[#965EA7]"
              data-testid="enr-referral-link-input"
            />
            <button type="button" onClick={() => link()} disabled={busy || linkCode.trim().length < 4}
              className="rounded-full border border-[#965EA7] px-3 py-1.5 text-xs font-semibold text-[#965EA7] hover:bg-[#F0E7F4] disabled:opacity-50"
              data-testid="enr-referral-link">
              Leagă de acest cod
            </button>
            <span className="text-xs text-[#6B7280]">Pentru cine a uitat să treacă codul la înscriere.</span>
          </div>
        )}
      </div>

      <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">Codul lui</div>
      {state.code ? (
        <p className="text-sm text-[#373A45]">
          <span className="font-mono text-base font-semibold tracking-widest text-[#7A4986]" data-testid="enr-referral-code">
            {state.code}
          </span>
          <span className="ml-2 text-xs text-[#6B7280]">
            {state.sent_at ? `trimis pe ${fmtDay(state.sent_at)}` : "netrimis încă"}
            {state.until ? ` · poate fi folosit până la ${state.until}` : ""}
          </span>
          {referred && (
            <span className="mt-1 block text-xs text-[#373A45]" data-testid="enr-referral-count">
              A recomandat: <strong>{referred.entered}</strong>
              {referred.entered > 0 ? ` · ${referred.paid} din ${referred.required} plătite` : ""}
            </span>
          )}
        </p>
      ) : (
        <p className="text-sm text-[#6B7280]">Nu are încă un cod.</p>
      )}
      {!state.can_send && state.why_not && (
        <p className="mt-2 text-xs text-[#8a6a1a]" data-testid="enr-referral-why-not">{state.why_not}</p>
      )}
      {canSend && state.can_send && (
        <button
          type="button"
          onClick={send}
          disabled={busy}
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#965EA7] px-3 py-1.5 text-xs font-semibold text-[#965EA7] hover:bg-[#F0E7F4] disabled:opacity-50"
          data-testid="enr-referral-send-code"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          Trimite codul de recomandare
        </button>
      )}
    </div>
  );
}
